import { Ban } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { BlockCallerDialog } from "@/features/block-caller";
import type { AcdCall } from "@/lib/acd";
import { useAuth } from "@/lib/auth";
import {
  blockFor, isHiddenCaller, untilPhrase, type BlockTarget, type BlockedCaller,
} from "@/lib/blocked-callers";
import { useBlockOn } from "@/lib/blocked-callers-api";
import { isOutsideNumber } from "@/lib/phone-number";
import { accountScope } from "@/lib/session-cache";
import type { MissedCall } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useAcdCall } from "@/softphone/acd-call";
import { useSoftphone, type Call } from "@/softphone/softphone-context";

/**
 * Who a call on this phone can block: the outside caller of an inbound call, by number or as a
 * hidden caller. Null for a call the rep placed, a lead the dialer reached, a colleague, a room.
 * The ACD's record is the word on a call it rang the rep for; a call without one (a rep's own
 * number) is read off the call itself. Either way an empty number means "not said", not hidden.
 */
export function callBlockTarget(
  call: Pick<Call, "id" | "direction" | "remote" | "remoteName" | "internalTransfer">,
  acd: Pick<AcdCall, "callId" | "kind" | "callerNumber" | "leadId"> | null,
): BlockTarget | null {
  if (call.direction !== "inbound" || call.internalTransfer) return null;
  // The call itself goes with the block: core-api keeps it as blocked, and takes a caller still
  // waiting in a line off it.
  const callId = call.id;
  if (acd !== null && acd.callId === call.id) {
    if (acd.kind !== null && acd.kind !== "inbound") return null;
    if (isHiddenCaller(acd.callerNumber)) return { number: null, hidden: true, callId };
    if (!isOutsideNumber(acd.callerNumber)) return null;
    return { number: acd.callerNumber, hidden: false, leadId: acd.leadId, name: call.remoteName, callId };
  }
  if (isHiddenCaller(call.remote)) return { number: null, hidden: true, callId };
  if (!isOutsideNumber(call.remote)) return null;
  return { number: call.remote, hidden: false, name: call.remoteName, callId };
}

/**
 * Who a missed call can block: its caller, unless the dialer placed it or a colleague made it. A
 * row with no number offers nothing: that is a gap in what the server knows, not a hidden caller.
 */
export function missedCallBlockTarget(call: Pick<MissedCall, "caller_number" | "direction" | "lead_id" | "caller_name">): BlockTarget | null {
  if (call.direction === "outbound") return null;
  if (isHiddenCaller(call.caller_number)) return { number: null, hidden: true };
  if (!isOutsideNumber(call.caller_number)) return null;
  return { number: call.caller_number, hidden: false, leadId: call.lead_id, name: call.caller_name };
}

/* The block a phone screen asked for. Kept apart from the screen, as the "Create contact" form is:
   a caller hanging up takes the call screen away while the rep is still choosing. Like the form's
   answers and the Phone page's kept lead, it belongs to the account that asked (`accountScope`):
   somebody else signing in on this tab never finds it open. */

interface BlockAsk {
  /** The signed-in account that asked. */
  account: string;
  target: BlockTarget;
  /** The call the block was asked from, declined with it if it is still ringing then. */
  decline?: string;
}

let asked: BlockAsk | null = null;
const watchers = new Set<() => void>();

function setAsked(next: BlockAsk | null): void {
  asked = next;
  for (const notify of watchers) notify();
}

function watch(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

/** Open the block dialog from anywhere on the phone, for the account signed in now. */
export function askToBlock(account: string, target: BlockTarget, options: { decline?: string } = {}): void {
  setAsked({ account, target, decline: options.decline });
}

/** Forget the dialog asked for. For tests, which share the module between cases. */
export function resetBlockAsk(): void {
  setAsked(null);
}

/** `askToBlock` for the account signed in. */
function useAskToBlock(): (target: BlockTarget, options?: { decline?: string }) => void {
  const account = accountScope(useAuth().user ?? null);
  return (target, options) => askToBlock(account, target, options);
}

/**
 * The block dialog the phone's screens open, on the phone's outermost part so it outlives them.
 * A call it was asked from is declined once the block is saved, but only if it is still ringing
 * then, by the phone's state at that moment: the rep may have answered it meanwhile, and a
 * decline on an answered call would hang it up.
 */
export function BlockCallerHost() {
  const phone = useSoftphone();
  const user = useAuth().user ?? null;
  const account = accountScope(user);
  const ask = React.useSyncExternalStore(watch, () => asked, () => asked);
  // Another account's ask is not this one's, even for the one render before the effect drops it.
  const mine = ask !== null && ask.account === account ? ask : null;
  React.useEffect(() => {
    if (user !== null && ask !== null && ask.account !== account) setAsked(null);
  }, [user, ask, account]);
  if (mine === null) return null;
  return (
    <BlockCallerDialog
      open
      onOpenChange={(open) => !open && setAsked(null)}
      target={mine.target}
      onBlocked={() => {
        const ringing = phone.calls.find((c) => c.id === mine.decline && c.state === "ringing");
        if (ringing) void phone.reject(ringing.id);
      }}
    />
  );
}

/**
 * "Block caller" under an inbound call, ringing or live, for a caller from outside. A caller
 * already blocked says until when, and opens the same dialog. On a ringing call the block
 * declines the call too (`decline`); on a live one the rep ends it when they choose. Centred
 * under Answer and Decline; the Phone page puts it at the start, under the caller's card.
 */
export function BlockCallerAction({
  call, decline = false, className,
}: {
  call: Call;
  decline?: boolean;
  className?: string;
}) {
  const acd = useAcdCall();
  const ask = useAskToBlock();
  const target = callBlockTarget(call, acd);
  const existing = useBlockOn(target);
  if (target === null) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn("self-center", existing ? "text-danger-11" : "text-text-muted", className)}
      onClick={() => ask(target, decline ? { decline: call.id } : {})}
    >
      <Ban />
      {existing
        ? `Blocked · ${untilPhrase(existing)}`
        : target.hidden
          ? "Block hidden callers"
          : "Block caller"}
    </Button>
  );
}

/**
 * The block button on a missed call's row: over the row's link to the contact, as Call back is.
 * `blocks` is what the list asked about all its rows' callers at once (`useBlocksOn`).
 */
export function BlockMissedCallerButton({ call, blocks }: { call: MissedCall; blocks: readonly BlockedCaller[] | undefined }) {
  const ask = useAskToBlock();
  const target = missedCallBlockTarget(call);
  if (target === null) return null;
  const existing = blockFor(blocks, target);
  const label = existing
    ? `Blocked · ${untilPhrase(existing)}`
    : target.hidden
      ? "Block hidden callers"
      : "Block this caller";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      className={cn("relative", existing ? "text-danger-11" : "text-text-muted")}
      onClick={() => ask(target)}
    >
      <Ban />
    </Button>
  );
}
